import React, { useState, useEffect } from 'react';
import { 
  Waves, 
  Wallet, 
  ShieldCheck, 
  Cpu, 
  ArrowRight, 
  Mail, 
  Globe, 
  Users, 
  Stethoscope,
  ChevronRight,
  Menu,
  X,
  ShoppingBag,
  TrendingUp,
  Zap,
  Terminal,
  Settings,
  Server
} from 'lucide-react';

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Smooth scroll helper
  const scrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans selection:bg-blue-500/30">
      {/* Navigation */}
      <nav className="fixed w-full z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => scrollTo('home')}>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center">
                <Cpu className="text-white w-6 h-6" />
              </div>
              <span className="text-xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
                ECS
              </span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-8 text-sm font-medium">
              <button onClick={() => scrollTo('home')} className="hover:text-blue-400 transition-colors">Home</button>
              <button onClick={() => scrollTo('ventures')} className="hover:text-blue-400 transition-colors">Ventures</button>
              <button onClick={() => scrollTo('about')} className="hover:text-blue-400 transition-colors">About</button>
              <button 
                onClick={() => scrollTo('contact')}
                className="bg-blue-600 hover:bg-blue-500 px-5 py-2 rounded-full transition-all transform hover:scale-105 active:scale-95"
              >
                Get in Touch
              </button>
            </div>

            {/* Mobile Nav Toggle */}
            <div className="md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2">
                {isMenuOpen ? <X /> : <Menu />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-slate-900 border-b border-slate-800 py-4 px-4 space-y-4 animate-in slide-in-from-top duration-300">
            <button onClick={() => scrollTo('home')} className="block w-full text-left py-2">Home</button>
            <button onClick={() => scrollTo('ventures')} className="block w-full text-left py-2">Ventures</button>
            <button onClick={() => scrollTo('about')} className="block w-full text-left py-2">About</button>
            <button onClick={() => scrollTo('contact')} className="block w-full text-left py-2 text-blue-400">Contact</button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <header id="home" className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10 opacity-20">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600 rounded-full blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-cyan-600 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-900/50 text-blue-400 text-xs font-semibold mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
            </span>
            Based in New Zealand
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6">
            Emergent <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-400">Cognitive</span> Solutions
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            A venture studio and consultancy bridging the gap between cutting-edge AI technology, IT systems, and social inclusion.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => scrollTo('ventures')}
              className="bg-white text-slate-950 px-8 py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-slate-200 transition-all"
            >
              Explore Ventures <ArrowRight size={20} />
            </button>
            <button 
              onClick={() => scrollTo('contact')}
              className="border border-slate-700 px-8 py-4 rounded-xl font-bold hover:bg-slate-900 transition-all"
            >
              Contact ECS
            </button>
          </div>
        </div>
      </header>

      {/* Ventures Section */}
      <section id="ventures" className="py-24 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-4">
            <div>
              <h2 className="text-3xl font-bold mb-4">The ECS Ecosystem</h2>
              <p className="text-slate-400 max-w-lg">
                Purpose-built products and specialized consulting services designed to create synergy across our portfolio.
              </p>
            </div>
            <div className="text-slate-500 text-sm font-mono">
              PROJECT_STATUS: MULTI_VENTURE_PRODUCT_DEVELOPMENT
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Ocean Echo */}
            <div className="group relative bg-slate-900 border border-slate-800 rounded-3xl p-8 hover:border-blue-500/50 transition-all duration-500 overflow-hidden flex flex-col">
              <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                <Waves size={100} />
              </div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-blue-600/10 rounded-2xl flex items-center justify-center text-blue-500">
                  <Waves size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Ocean Echo</h3>
                  <span className="text-xs font-semibold text-blue-400 uppercase tracking-widest">Medical AI</span>
                </div>
              </div>
              <p className="text-slate-400 mb-8 leading-relaxed flex-grow text-sm">
                Localized inference AI designed to meet the legal privacy requirements for the NZ health market. Secure, offline-capable speech processing.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-xs text-slate-300">
                  <ShieldCheck size={16} className="text-green-500" /> NZ Health Privacy Compliant
                </li>
                <li className="flex items-center gap-2 text-xs text-slate-300">
                  <Stethoscope size={16} className="text-green-500" /> Local Inference Processing
                </li>
              </ul>
              <button className="flex items-center gap-2 text-blue-400 font-semibold text-sm group-hover:gap-3 transition-all">
                Learn more <ChevronRight size={16} />
              </button>
            </div>

            {/* iBeg */}
            <div className="group relative bg-slate-900 border border-slate-800 rounded-3xl p-8 hover:border-cyan-500/50 transition-all duration-500 overflow-hidden flex flex-col">
              <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                <Wallet size={100} />
              </div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-cyan-600/10 rounded-2xl flex items-center justify-center text-cyan-500">
                  <Wallet size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">iBeg</h3>
                  <span className="text-xs font-semibold text-cyan-400 uppercase tracking-widest">Fintech</span>
                </div>
              </div>
              <p className="text-slate-400 mb-8 leading-relaxed flex-grow text-sm">
                Bridging the gap in a cashless world. iBeg enables digital payment collection for vulnerable communities via secure contactless tech.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-xs text-slate-300">
                  <Users size={16} className="text-green-500" /> Social Inclusion Focus
                </li>
                <li className="flex items-center gap-2 text-xs text-slate-300">
                  <ShieldCheck size={16} className="text-green-500" /> Secure Micro-payments
                </li>
              </ul>
              <button className="flex items-center gap-2 text-cyan-400 font-semibold text-sm group-hover:gap-3 transition-all">
                Learn more <ChevronRight size={16} />
              </button>
            </div>

            {/* Garage */}
            <div className="group relative bg-slate-900 border border-slate-800 rounded-3xl p-8 hover:border-purple-500/50 transition-all duration-500 overflow-hidden flex flex-col">
              <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                <ShoppingBag size={100} />
              </div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-purple-600/10 rounded-2xl flex items-center justify-center text-purple-500">
                  <ShoppingBag size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Garage</h3>
                  <span className="text-xs font-semibold text-purple-400 uppercase tracking-widest">Marketplace</span>
                </div>
              </div>
              <p className="text-slate-400 mb-8 leading-relaxed flex-grow text-sm">
                A disruptive online marketplace (garage.net.nz) for New Zealand. Designed to challenge incumbents and drive ecosystem-wide revenue.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-xs text-slate-300">
                  <Zap size={16} className="text-green-500" /> Market Disruption Engine
                </li>
                <li className="flex items-center gap-2 text-xs text-slate-300">
                  <TrendingUp size={16} className="text-green-500" /> Venture Revenue Synergy
                </li>
              </ul>
              <a href="https://garage.net.nz" target="_blank" className="flex items-center gap-2 text-purple-400 font-semibold text-sm group-hover:gap-3 transition-all">
                Visit garage.net.nz <ChevronRight size={16} />
              </a>
            </div>

            {/* ECS Consulting */}
            <div className="group relative bg-slate-900 border border-slate-800 rounded-3xl p-8 hover:border-emerald-500/50 transition-all duration-500 overflow-hidden flex flex-col">
              <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                <Terminal size={100} />
              </div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-emerald-600/10 rounded-2xl flex items-center justify-center text-emerald-500">
                  <Terminal size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">ECS Systems</h3>
                  <span className="text-xs font-semibold text-emerald-400 uppercase tracking-widest">Consulting</span>
                </div>
              </div>
              <p className="text-slate-400 mb-8 leading-relaxed flex-grow text-sm">
                Expert IT DevOps and AIOps consulting. We design robust systems and automated operations to keep modern enterprises agile and resilient.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-xs text-slate-300">
                  <Settings size={16} className="text-green-500" /> DevOps & AIOps Pipelines
                </li>
                <li className="flex items-center gap-2 text-xs text-slate-300">
                  <Server size={16} className="text-green-500" /> Scalable Systems Architecture
                </li>
              </ul>
              <button className="flex items-center gap-2 text-emerald-400 font-semibold text-sm group-hover:gap-3 transition-all">
                View Services <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-blue-600/20 to-slate-800 rounded-3xl border border-slate-700 flex items-center justify-center">
                 <div className="text-center p-10">
                    <div className="text-6xl mb-4">🛸</div>
                    <h4 className="text-2xl font-bold mb-2">The ECS Vision</h4>
                    <p className="text-slate-400">Merging product innovation with operational excellence.</p>
                 </div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-600/20 blur-3xl -z-10" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6">Unified Vision, Collective Impact</h2>
              <p className="text-slate-400 mb-6 leading-relaxed text-lg">
                Emergent Cognitive Solutions operates as a strategic incubator and consultancy. We don't just build apps; we build interconnected ventures and robust systems that support each other.
              </p>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 shrink-0 bg-slate-800 rounded-lg flex items-center justify-center text-blue-400">
                    <TrendingUp size={24} />
                  </div>
                  <div>
                    <h5 className="font-bold mb-1">Synergistic Growth</h5>
                    <p className="text-slate-400 text-sm">Our consulting arm funds and battle-tests the same technologies used in our specialized products.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 shrink-0 bg-slate-800 rounded-lg flex items-center justify-center text-blue-400">
                    <Globe size={24} />
                  </div>
                  <div>
                    <h5 className="font-bold mb-1">Operational Excellence</h5>
                    <p className="text-slate-400 text-sm">Bringing enterprise-grade DevOps and AIOps standards to every New Zealand project.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-20" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-8">Let's Connect</h2>
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-10">
            <p className="text-slate-400 mb-8">
              Interested in collaborating, investing, or consulting services? Reach out directly.
            </p>
            <div className="flex flex-col md:flex-row gap-6 justify-center">
              <a 
                href="mailto:contact@emergent.net.nz" 
                className="flex items-center justify-center gap-3 px-6 py-4 bg-slate-800 hover:bg-slate-700 rounded-2xl transition-all"
              >
                <Mail className="text-blue-400" />
                <span>contact@emergent.net.nz</span>
              </a>
              <div className="flex items-center justify-center gap-3 px-6 py-4 bg-slate-800 rounded-2xl">
                <Globe className="text-blue-400" />
                <span>emergent.net.nz</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-2 opacity-60">
            <Cpu className="w-5 h-5" />
            <span className="text-sm font-semibold uppercase tracking-widest">ECS</span>
          </div>
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Emergent Cognitive Solutions. All rights reserved.
          </p>
          <div className="flex gap-6 text-slate-500 text-sm">
            <a href="#" className="hover:text-blue-400">Privacy</a>
            <a href="#" className="hover:text-blue-400">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;